import { Dispatch } from "react";
import { AppState } from "./configureStore";
import { createSlice, PayloadAction, AnyAction } from '@reduxjs/toolkit';
import { ILoadableState } from "../definitions/ILoadableState";
import { client, Path } from "../App";
import {
    GetCurrentTimeResponse, GetCurrentTime,
    driverSummaryContrib, CMWespathResponse,
    CMWespath, PaymentUpdateResponse, PaymentUpdate,
    HolidayListResponse, HolidayList,
    SelectedBankResponse, SelectedBank
} from "../DCR.dtos"
import { push } from "connected-react-router";

export interface ISchedulePaymentState extends ILoadableState {
    cmGetCurrentTimeInfo: GetCurrentTimeResponse;
    cmWespathInfo: CMWespathResponse;
    cmPaymentUpdateInfo: PaymentUpdateResponse;
    cmHolidayListInfo: HolidayListResponse;
    cmSelectedBankInfo: SelectedBankResponse;
    isPaymentLoaded: boolean;
    isGetCurrentTimeLoaded: boolean;
    isHolidayListLoaded: boolean;
    isWespathInfoLoaded: boolean;
    isPaymentLoding: boolean;
    isPaymentUpdateError: boolean;
    isWespathInfoError: boolean;
    paymentUpdateErrorMsg: string;
    isSelectedBankLoaded: boolean;
    isGetCurrentTimeError: boolean;
    selectedBankErrorMsg: string;
    ccaId: string;
    cmContribs: driverSummaryContrib[];
    cmOffset: number;
    filePayrollDate: string;
    statusDate: string;
    scheduleDate: string;

}

const initialSchedulePaymentState: ISchedulePaymentState = {
    cmGetCurrentTimeInfo: {} as GetCurrentTimeResponse,
    cmWespathInfo: {} as CMWespathResponse,
    cmPaymentUpdateInfo: {} as PaymentUpdateResponse,
    cmHolidayListInfo: {} as HolidayListResponse,
    cmSelectedBankInfo: {} as SelectedBankResponse,
    isPaymentLoaded: false,
    isPaymentLoding: false,
    isGetCurrentTimeLoaded: false,
    isPaymentUpdateError: false,
    isWespathInfoLoaded: false,
    isWespathInfoError: false,
    isSelectedBankLoaded: false,
    isGetCurrentTimeError: false,
    paymentUpdateErrorMsg: '',
    selectedBankErrorMsg: '',
    isHolidayListLoaded: false,
    isLoading: false,
    isLoaded: false,
    cmContribs: [],
    ccaId: '',
    cmOffset: 0,
    filePayrollDate: '',
    statusDate: '',
    scheduleDate: ''

}

const slice = createSlice({
    name: "schedulepayment",
    initialState: initialSchedulePaymentState,
    reducers: {
        resetState: () => {
            return {
                ...initialSchedulePaymentState
            }
        },

        requestCMWespath: (state: ISchedulePaymentState) => {
            return {
                ...state,
                isWespathInfoLoaded: false,
                isWespathInfoError: false
            }
        },
        receiveCMWespath: (state: ISchedulePaymentState, action: PayloadAction<{
            cmWespathInfoResponse: CMWespathResponse;
        }>) => {
            const cmWespathInfo = action.payload.cmWespathInfoResponse;
            return {
                ...state,
                cmWespathInfo,
                isWespathInfoLoaded: true,
                isWespathInfoError: false
            }
        },
        receiveCMWespathError: (state: ISchedulePaymentState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isWespathInfoLoaded: true,
                isWespathInfoError: true,
                paymentUpdateErrorMsg: message

            }
        },
        requestCMGetCurrentTime: (state: ISchedulePaymentState) => {
            return {
                ...state,
                isGetCurrentTimeLoaded: false,
                isGetCurrentTimeError: false
            }
        },
        receiveCMGetCurrentTime: (state: ISchedulePaymentState, action: PayloadAction<{
            cmGetCurrentTimeInfoResponse: GetCurrentTimeResponse;
        }>) => {
            const cmGetCurrentTimeInfo = action.payload.cmGetCurrentTimeInfoResponse;
            return {
                ...state,
                cmGetCurrentTimeInfo,
                isGetCurrentTimeLoaded: true,
                isGetCurrentTimeError: false
            }
        },
        receiveCMGetCurrentTimeError: (state: ISchedulePaymentState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isGetCurrentTimeLoaded: true,
                isGetCurrentTimeError: true,
                paymentUpdateErrorMsg: message
            }
        },
        requestCMPaymentUpdate: (state: ISchedulePaymentState) => {
            return {
                ...state,
                isPaymentLoaded: false,
                isPaymentLoding: true
            }
        },
        receiveCMPaymentUpdate: (state: ISchedulePaymentState, action: PayloadAction<{
            cmPaymentUpdateInfoResponse: PaymentUpdateResponse;
        }>) => {
            const cmPaymentUpdateInfo = action.payload.cmPaymentUpdateInfoResponse;
            return {
                ...state,
                cmPaymentUpdateInfo,
                isPaymentLoaded: true,
                isPaymentUpdateError: false,
                isPaymentLoding: false
            }
        },
        receiveCMPaymentUpdateError: (state: ISchedulePaymentState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isPaymentLoaded: true,
                isPaymentUpdateError: true,
                isPaymentLoding: false,
                paymentUpdateErrorMsg: message
            }
        },
        requestCMHolidayList: (state: ISchedulePaymentState) => {
            return {
                ...state,
                isHolidayListLoaded: false
            }
        },
        receiveCMHolidayList: (state: ISchedulePaymentState, action: PayloadAction<{
            cmHolidayListInfoResponse: HolidayListResponse;
        }>) => {
            const cmHolidayListInfo = action.payload.cmHolidayListInfoResponse;
            return {
                ...state,
                cmHolidayListInfo,
                isHolidayListLoaded: true
            }
        },
        receiveCMHolidayListError: (state: ISchedulePaymentState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isHolidayListLoaded: true,
                paymentUpdateErrorMsg: message
            }
        },

        requestCMSelectedBank: (state: ISchedulePaymentState) => {
            return {
                ...state,
                isSelectedBankLoaded: false
            }
        },
        receiveCMSelectedBank: (state: ISchedulePaymentState, action: PayloadAction<{
            cmSelectedBankInfoResponse: SelectedBankResponse;
        }>) => {
            const cmSelectedBankInfo = action.payload.cmSelectedBankInfoResponse;
            return {
                ...state,
                cmSelectedBankInfo,
                isSelectedBankLoaded: true
            }
        },
        receiveCMSelectedBankError: (state: ISchedulePaymentState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isSelectedBankLoaded: true,
                selectedBankErrorMsg: message
            }
        },

        receiveDefaultCMSummary: (state: ISchedulePaymentState, action: PayloadAction<{ contribs: driverSummaryContrib[]; offset: number; filePayrollDate: string; ccaId: string; statusDate: string; scheduleDate: string }>) => {
            const { contribs, offset, filePayrollDate, ccaId, statusDate, scheduleDate } = action.payload;
            return {
                ...state,
                cmContribs: contribs,
                cmOffset: offset,
                filePayrollDate: filePayrollDate,
                ccaId: ccaId,
                statusDate: statusDate,
                scheduleDate: scheduleDate
            }
        }
    }
})

const {
    resetState,
    requestCMWespath,
    receiveCMWespath,
    receiveCMWespathError,
    requestCMGetCurrentTime,
    receiveCMGetCurrentTime,
    receiveCMGetCurrentTimeError,
    requestCMPaymentUpdate,
    receiveCMPaymentUpdate,
    receiveCMPaymentUpdateError,
    receiveDefaultCMSummary,
    requestCMHolidayList,
    receiveCMHolidayList,
    receiveCMHolidayListError,
    requestCMSelectedBank,
    receiveCMSelectedBank,
    receiveCMSelectedBankError

} = slice.actions;

function setCmSchedulePaymentDefaults(contribs: driverSummaryContrib[], offset: number, filePayrollDate: string, ccaId: string, statusDate: string, scheduleDate: string) {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(receiveDefaultCMSummary({ contribs, offset, filePayrollDate, ccaId, statusDate, scheduleDate }));
    }
}

function resetAllStateAndRedirectToHistory() {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(resetState());
        dispatch(push(Path.ContributionHistory));
    }
}
function resetAllStateAndRedirectToDetails() {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(resetState());
        dispatch(push(Path.ContributionDetails));
    }
}

function fetchCMWespath(accountnumber: string, debitamount: string) {
    return async (dispatch: Dispatch<AnyAction>) => {

        dispatch(requestCMWespath());

        try {
            const cmWespathInfoResponse = await client.post(new CMWespath({ accountnumber: accountnumber, debitamount: debitamount }));

            //Check for errors
            if (cmWespathInfoResponse.responseStatus?.message) {
                throw cmWespathInfoResponse;
            }

            dispatch(receiveCMWespath({ cmWespathInfoResponse }));
        }
        catch (e) {
            console.log(e);
            dispatch(receiveCMWespathError({ message: e }))
        }
    }
}


function fetchCMGetCurrentTime() {
    return async (dispatch: Dispatch<AnyAction>) => {

        dispatch(requestCMGetCurrentTime());

        try {
            const cmGetCurrentTimeInfoResponse = await client.get(new GetCurrentTime());

            dispatch(receiveCMGetCurrentTime({ cmGetCurrentTimeInfoResponse }));
        }
        catch (e) {
            console.log(e);
            dispatch(receiveCMGetCurrentTimeError({ message: e }))
        }
    }
}

function fetchCMPaymentUpdate(tran: string, deliverbydt: string) {
    return async (dispatch: Dispatch<AnyAction>) => {

        dispatch(requestCMPaymentUpdate());

        try {
            const cmPaymentUpdateInfoResponse = await client.post(new PaymentUpdate({ tran: tran, deliverbydt: deliverbydt }));

            //Check for errors
            if (cmPaymentUpdateInfoResponse.responseStatus) {
                throw cmPaymentUpdateInfoResponse.responseStatus.message;
            }
            dispatch(receiveCMPaymentUpdate({ cmPaymentUpdateInfoResponse }));
        }
        catch (e) {
            console.log(e);
            dispatch(receiveCMPaymentUpdateError({ message: e }))
        }
    }
}

function fetchCMHolidayList(checkLoading: boolean) {
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const cmCMHolidayListState = getState().cmSchedulePayment;
        if (checkLoading && (cmCMHolidayListState.isHolidayListLoaded)) {
            return;
        }

        dispatch(requestCMHolidayList());

        try {
            const cmHolidayListInfoResponse = await client.get(new HolidayList());
            //Check for errors
            if (cmHolidayListInfoResponse.responseStatus) {
                throw cmHolidayListInfoResponse.responseStatus.message;
            }
            dispatch(receiveCMHolidayList({ cmHolidayListInfoResponse }));
        }
        catch (e) {
            console.log(e);
            dispatch(receiveCMHolidayListError({ message: e }))
        }
    }
}

function fetchCMSelectedBank(ccaid: string, checkLoading: boolean) {
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const cmSchedulePaymentState = getState().cmSchedulePayment;
        if (checkLoading && (cmSchedulePaymentState.isSelectedBankLoaded)) {
            return;
        }

        dispatch(requestCMSelectedBank());

        try {
            const cmSelectedBankInfoResponse = await client.get(new SelectedBank({ ccaid: ccaid }));

            //Check for errors
            if (cmSelectedBankInfoResponse.responseStatus?.message) {
                throw cmSelectedBankInfoResponse;
            }
            dispatch(receiveCMSelectedBank({ cmSelectedBankInfoResponse }));
        }
        catch (e) {
            console.log(e);
            dispatch(receiveCMSelectedBankError({ message: e }))
        }
    }
}

export const schedulePaymentReducer = slice.reducer;

export const schedulePaymentActionCreators = {
    fetchCMWespath,
    fetchCMGetCurrentTime,
    setCmSchedulePaymentDefaults,
    fetchCMHolidayList,
    fetchCMPaymentUpdate,
    fetchCMSelectedBank,
    resetAllStateAndRedirectToHistory,
    resetAllStateAndRedirectToDetails,
    resetState
};